import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { io as socketIO } from 'socket.io-client';
import { useConfirm } from '../components/Modal';

// Same base as services/api.js. Relative '/api' URLs only work behind the web
// server's proxy, not in the Android app (or a dev server without a proxy).
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const S = {
  page: { maxWidth: 1200, margin: '0 auto', padding: 24, fontFamily: 'system-ui, sans-serif' },
  header: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24, flexWrap: 'wrap' },
  backBtn: { background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: 14, padding: '4px 0' },
  h1: { fontSize: '1.6rem', fontWeight: 700, margin: 0, color: '#1f2937' },
  refreshBtn: { marginLeft: 'auto', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 8, padding: '8px 14px', cursor: 'pointer', fontSize: 13 },
  grid: { display: 'flex', gap: 0, border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden', minHeight: 600 },
  sidebar: { width: 200, background: '#1f2937', padding: '20px 0', flexShrink: 0 },
  sidebarTitle: { color: '#f9fafb', fontWeight: 700, padding: '0 16px 16px', fontSize: 15, borderBottom: '1px solid #374151', marginBottom: 8 },
  statRow: { display: 'flex', justifyContent: 'space-between', padding: '4px 16px' },
  statLabel: { color: '#9ca3af', fontSize: 12 },
  statVal: { color: '#f9fafb', fontSize: 12, fontWeight: 600 },
  navBtn: (active) => ({ display: 'block', width: '100%', textAlign: 'left', padding: '10px 16px', background: active ? '#374151' : 'none', border: 'none', color: active ? '#fff' : '#d1d5db', cursor: 'pointer', fontSize: 14 }),
  listPane: { width: 320, borderRight: '1px solid #e5e7eb', display: 'flex', flexDirection: 'column', flexShrink: 0 },
  searchRow: { padding: 12, borderBottom: '1px solid #e5e7eb', display: 'flex', gap: 6 },
  searchInput: { flex: 1, border: '1px solid #d1d5db', borderRadius: 6, padding: '7px 10px', fontSize: 13 },
  searchBtn: { background: '#DC143C', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 12px', cursor: 'pointer', fontSize: 13 },
  emailList: { flex: 1, overflowY: 'auto' },
  emailItem: (selected, unread) => ({ padding: '12px 14px', borderBottom: '1px solid #f3f4f6', cursor: 'pointer', background: selected ? '#eff6ff' : unread ? '#fefce8' : '#fff', borderLeft: selected ? '3px solid #DC143C' : '3px solid transparent' }),
  sender: (unread) => ({ fontWeight: unread ? 700 : 500, fontSize: 13, color: '#111827', marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }),
  subjectLine: (unread) => ({ fontWeight: unread ? 600 : 400, fontSize: 12, color: '#374151', marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }),
  preview: { fontSize: 11, color: '#9ca3af', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  dateStamp: { fontSize: 10, color: '#d1d5db', textAlign: 'right', marginTop: 2 },
  pagination: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 10, borderTop: '1px solid #e5e7eb', fontSize: 12, color: '#6b7280' },
  pageBtn: (disabled) => ({ background: disabled ? '#f3f4f6' : '#fff', border: '1px solid #d1d5db', borderRadius: 6, padding: '4px 10px', cursor: disabled ? 'default' : 'pointer', color: disabled ? '#9ca3af' : '#374151', fontSize: 12 }),
  contentPane: { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  emailHeader: { padding: '16px 20px', borderBottom: '1px solid #e5e7eb', background: '#fafafa' },
  emailSubject: { fontSize: '1.1rem', fontWeight: 700, color: '#111827', marginBottom: 8 },
  metaRow: { fontSize: 13, color: '#6b7280', marginBottom: 3 },
  actionRow: { display: 'flex', gap: 8, marginTop: 12 },
  btnSec: { background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 12 },
  btnDanger: { background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 12, color: '#b91c1c' },
  emailBody: { flex: 1, padding: 20, overflowY: 'auto', background: '#f9fafb', borderRadius: 8 },
  emailText: { whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 14, color: '#1f2937', lineHeight: 1.6 },
  emptyPane: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#9ca3af' },
  spamBadge: { display: 'inline-block', background: '#fee2e2', color: '#b91c1c', fontSize: 11, padding: '2px 8px', borderRadius: 999, marginLeft: 8 },
  exportBtn: { display: 'block', width: '100%', textAlign: 'left', padding: '10px 16px', background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: 13, borderTop: '1px solid #374151', marginTop: 8 },
};

export default function EmailDashboard() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [confirmDialog, ConfirmDialog] = useConfirm();
  const [emails, setEmails] = useState([]);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [folder, setFolder] = useState('inbox');
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [stats, setStats] = useState({ total: 0, unread: 0, inbox: 0, sent: 0, spam: 0 });
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const [newMsgBanner, setNewMsgBanner] = useState(false);
  const [compose, setCompose] = useState(false);
  const [composeTo, setComposeTo] = useState('');
  const [composeSubject, setComposeSubject] = useState('');
  const [composeBody, setComposeBody] = useState('');
  const [composeSending, setComposeSending] = useState(false);
  const [socketConnected, setSocketConnected] = useState(false);
  const prevUnreadRef = useRef(null);
  const socketRef = useRef(null);
  const folderRef = useRef(folder);
  const LIMIT = 20;

  // Keep folderRef in sync so the socket handler can see current folder
  useEffect(() => { folderRef.current = folder; }, [folder]);

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.role !== 'admin') { navigate('/login'); return; }
    loadEmails();
    loadStats();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading, navigate, folder, offset]);

  // ── Socket.IO real-time connection ──────────────────────────────────────────
  useEffect(() => {
    if (!user || user.role !== 'admin') return;
    const token = localStorage.getItem('nepwed_token');
    const serverUrl = API_BASE.replace(/\/api\/?$/, '') || undefined; // as in services/socket.js
    const socket = socketIO(serverUrl, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnectionDelay: 2000,
    });
    socketRef.current = socket;

    socket.on('connect', () => setSocketConnected(true));
    socket.on('disconnect', () => setSocketConnected(false));

    socket.on('new_email', (email) => {
      // Bump unread count and show banner
      setStats(prev => ({
        ...prev,
        unread: parseInt(prev.unread || 0) + 1,
        inbox: parseInt(prev.inbox || 0) + 1,
        total: parseInt(prev.total || 0) + 1,
      }));
      setNewMsgBanner(true);
      setTimeout(() => setNewMsgBanner(false), 8000);

      // If currently showing inbox, prepend the new email without a full reload
      if (folderRef.current === 'inbox') {
        setEmails(prev => [{ ...email, status: 'unread', preview: email.preview }, ...prev]);
        setTotal(prev => prev + 1);
      }
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── 30 s polling fallback (stats refresh when socket is NOT connected) ──────
  useEffect(() => {
    if (!user || user.role !== 'admin') return;
    const interval = setInterval(async () => {
      if (socketConnected) return; // socket is live — no need to poll
      try {
        const t = localStorage.getItem('nepwed_token');
        const res = await fetch(`${API_BASE}/email-admin/stats`, { headers: { Authorization: `Bearer ${t}` } });
        const data = await res.json();
        if (!data.error) {
          const newUnread = parseInt(data.unread || 0);
          if (prevUnreadRef.current !== null && newUnread > prevUnreadRef.current) {
            setNewMsgBanner(true);
            setTimeout(() => setNewMsgBanner(false), 8000);
          }
          prevUnreadRef.current = newUnread;
          setStats(data);
        }
      } catch (_) {}
    }, 30000);
    return () => clearInterval(interval);
  }, [user, socketConnected]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadEmails = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('nepwed_token');
      const response = await fetch(
        `${API_BASE}/email-admin/emails?folder=${folder}&offset=${offset}&limit=${LIMIT}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const data = await response.json();
      setEmails(data.emails || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Failed to load emails', err);
      setEmails([]);
    } finally {
      setLoading(false);
    }
  }, [folder, offset]);

  const loadStats = useCallback(async () => {
    try {
      const token = localStorage.getItem('nepwed_token');
      const response = await fetch(`${API_BASE}/email-admin/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (!data.error) {
        setStats(data);
        prevUnreadRef.current = parseInt(data.unread || 0);
      }
    } catch (err) {
      console.error('Failed to load stats', err);
    }
  }, []);

  const token = () => localStorage.getItem('nepwed_token');

  const handleSearch = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(
        `${API_BASE}/email-admin/emails?folder=${folder}&offset=0&limit=${LIMIT}${searchQuery.trim() ? `&q=${encodeURIComponent(searchQuery.trim())}` : ''}`,
        { headers: { 'Authorization': `Bearer ${token()}` } }
      );
      const data = await res.json();
      setEmails(data.emails || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Search failed', err);
      setEmails([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectEmail = async (email) => {
    // Fetch full email body (list only has 120-char preview)
    try {
      const res = await fetch(`${API_BASE}/email-admin/emails/${email.id}`, {
        headers: { 'Authorization': `Bearer ${token()}` }
      });
      const full = await res.json();
      if (full && !full.error) {
        setSelectedEmail(full);
      } else {
        setSelectedEmail(email);
      }
    } catch {
      setSelectedEmail(email);
    }
    if (email.status === 'unread') {
      try {
        await fetch(`${API_BASE}/email-admin/emails/${email.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token()}` },
          body: JSON.stringify({ status: 'read' })
        });
        setEmails(prev => prev.map(e => e.id === email.id ? { ...e, status: 'read' } : e));
        setStats(prev => ({ ...prev, unread: Math.max(0, parseInt(prev.unread || 1) - 1) }));
      } catch (err) { console.error('Failed to mark read', err); }
    }
  };

  const handleToggleRead = async () => {
    if (!selectedEmail) return;
    const newStatus = selectedEmail.status === 'unread' ? 'read' : 'unread';
    try {
      await fetch(`${API_BASE}/email-admin/emails/${selectedEmail.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token()}` },
        body: JSON.stringify({ status: newStatus })
      });
      setEmails(prev => prev.map(e => e.id === selectedEmail.id ? { ...e, status: newStatus } : e));
      setSelectedEmail(prev => ({ ...prev, status: newStatus }));
    } catch (err) { console.error('Failed to update', err); }
  };

  const handleDelete = async (id) => {
    if (!await confirmDialog({ title: 'Delete Email', message: 'Delete this email?', danger: true, confirmText: 'Delete' })) return;
    try {
      await fetch(`${API_BASE}/email-admin/emails/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token()}` }
      });
      setEmails(prev => prev.filter(e => e.id !== id));
      if (selectedEmail?.id === id) setSelectedEmail(null);
      setTotal(prev => prev - 1);
    } catch (err) { console.error('Failed to delete', err); }
  };

  const handleExport = async () => {
    try {
      const res = await fetch(`${API_BASE}/email-admin/export`, {
        headers: { 'Authorization': `Bearer ${token()}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `emails-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Export failed', err);
      alert('Export failed: ' + err.message);
    }
  };

  const handleCompose = async (e) => {
    e.preventDefault();
    if (!composeTo || !composeSubject || !composeBody) return;
    setComposeSending(true);
    try {
      const res = await fetch(`${API_BASE}/email-admin/compose`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token()}` },
        body: JSON.stringify({ to: composeTo, subject: composeSubject, body: composeBody })
      });
      const data = await res.json();
      if (data.ok) {
        setCompose(false);
        setComposeTo(''); setComposeSubject(''); setComposeBody('');
        if (folder === 'sent') loadEmails();
        loadStats();
        alert('Email sent!');
      } else {
        alert('Failed to send: ' + (data.error || 'Unknown error'));
      }
    } catch (err) {
      alert('Failed to send email');
    } finally {
      setComposeSending(false);
    }
  };

  const totalPages = Math.ceil(total / LIMIT);
  const currentPage = Math.floor(offset / LIMIT) + 1;

  if (authLoading) return <div style={{ padding: 40, textAlign: 'center' }}>Loading...</div>;

  const folders = [
    { key: 'inbox',  label: '📥 Inbox',    count: stats.inbox  || 0 },
    { key: 'unread', label: '📬 Unread',   count: stats.unread || 0 },
    { key: 'sent',   label: '📤 Sent',     count: stats.sent   || 0 },
    { key: 'spam',   label: '⚠ Spam',      count: stats.spam   || 0 },
    { key: 'all',    label: '📋 All Mail', count: stats.total  || 0 },
  ];

  return (
    <div style={S.page}>
      {newMsgBanner && (
        <div style={{ background: '#d1fae5', border: '1px solid #6ee7b7', borderRadius: 8, padding: '10px 16px', marginBottom: 12, fontSize: 13, color: '#065f46', display: 'flex', alignItems: 'center', gap: 8 }}>
          📩 New messages arrived — <button style={{ background: 'none', border: 'none', color: '#065f46', fontWeight: 700, cursor: 'pointer', padding: 0 }} onClick={() => { setFolder('inbox'); setOffset(0); loadEmails(); setNewMsgBanner(false); }}>View Inbox</button>
        </div>
      )}
      <div style={S.header}>
        <button style={S.backBtn} onClick={() => navigate('/admin')}>← Admin</button>
        <h1 style={S.h1}>📧 Mailbox</h1>
        <span style={{ fontSize: 11, padding: '3px 8px', borderRadius: 999, background: socketConnected ? '#d1fae5' : '#f3f4f6', color: socketConnected ? '#065f46' : '#6b7280', fontWeight: 600 }}>
          {socketConnected ? '● Live' : '○ Polling'}
        </span>
        <button style={{ background: '#DC143C', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 600, marginLeft: 8 }} onClick={() => setCompose(true)}>✏ Compose</button>
        <button style={S.refreshBtn} onClick={() => { loadEmails(); loadStats(); }}>↻ Refresh</button>
      </div>

      <div style={S.grid}>
        {/* Sidebar */}
        <div style={S.sidebar}>
          <div style={S.sidebarTitle}>Mailbox</div>
          <div style={{ padding: '8px 0 12px' }}>
            <div style={S.statRow}><span style={S.statLabel}>Unread</span><span style={{ ...S.statVal, color: (stats.unread || 0) > 0 ? '#fbbf24' : '#f9fafb' }}>{stats.unread || 0}</span></div>
            <div style={S.statRow}><span style={S.statLabel}>Total</span><span style={S.statVal}>{stats.total || 0}</span></div>
          </div>
          {folders.map(f => (
            <button key={f.key} style={S.navBtn(folder === f.key)} onClick={() => { setFolder(f.key); setOffset(0); setSelectedEmail(null); setSearchQuery(''); }}>
              <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>{f.label}</span>
                {f.count > 0 && <span style={{ background: folder === f.key ? '#DC143C' : '#4b5563', color: '#fff', borderRadius: 999, padding: '0 7px', fontSize: 11, fontWeight: 700 }}>{f.count}</span>}
              </span>
            </button>
          ))}
          <button style={S.exportBtn} onClick={handleExport}>⬇ Export JSON</button>
        </div>

        {/* Email list */}
        <div style={S.listPane}>
          <div style={S.searchRow}>
            <form onSubmit={handleSearch} style={{ display: 'flex', gap: 6, width: '100%' }}>
              <input
                style={S.searchInput}
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
              <button type="submit" style={S.searchBtn}>🔍</button>
            </form>
          </div>

          <div style={S.emailList}>
            {loading ? (
              <div style={{ padding: 20, color: '#9ca3af', fontSize: 13 }}>Loading…</div>
            ) : emails.length === 0 ? (
              <div style={{ padding: 20, color: '#9ca3af', fontSize: 13 }}>No emails found</div>
            ) : emails.map(email => {
              const unread = email.status === 'unread';
              const selected = selectedEmail?.id === email.id;
              return (
                <div key={email.id} style={S.emailItem(selected, unread)} onClick={() => handleSelectEmail(email)}>
                  <div style={S.sender(unread)}>
                    {folder === 'sent' ? `To: ${fmtName(email.recipient)}` : fmtName(email.sender)}
                  </div>
                  <div style={S.subjectLine(unread)}>{email.subject || '(No Subject)'}</div>
                  <div style={S.preview}>{(email.body || '').substring(0, 60)}</div>
                  <div style={S.dateStamp}>{fmtDate(email.created_at)}</div>
                </div>
              );
            })}
          </div>

          {totalPages > 1 && (
            <div style={S.pagination}>
              <button style={S.pageBtn(currentPage === 1)} disabled={currentPage === 1} onClick={() => setOffset(o => Math.max(0, o - LIMIT))}>← Prev</button>
              <span>{currentPage} / {totalPages}</span>
              <button style={S.pageBtn(currentPage === totalPages)} disabled={currentPage === totalPages} onClick={() => setOffset(o => o + LIMIT)}>Next →</button>
            </div>
          )}
        </div>

        {/* Email content */}
        <div style={S.contentPane}>
          {selectedEmail ? (
            <>
              <div style={S.emailHeader}>
                <div style={S.emailSubject}>{selectedEmail.subject || '(No Subject)'}</div>
                <div style={S.metaRow}><strong>From:</strong> {selectedEmail.sender}</div>
                <div style={S.metaRow}><strong>To:</strong> {selectedEmail.recipient}</div>
                <div style={S.metaRow}><strong>Date:</strong> {fmtDateTime(selectedEmail.created_at)}</div>
                {selectedEmail.is_spam && <span style={S.spamBadge}>⚠ Spam</span>}
                <div style={S.actionRow}>
                  <button style={S.btnSec} onClick={handleToggleRead}>
                    {selectedEmail.status === 'unread' ? '✓ Mark Read' : '◯ Mark Unread'}
                  </button>
                  <button style={S.btnDanger} onClick={() => handleDelete(selectedEmail.id)}>🗑 Delete</button>
                </div>
              </div>
              <div style={S.emailBody}>
                {selectedEmail.html_body
                  ? <iframe title="email" srcDoc={selectedEmail.html_body} style={{ width: '100%', height: '100%', minHeight: 400, border: 'none' }} sandbox="allow-same-origin" />
                  : <pre style={S.emailText}>{selectedEmail.body}</pre>
                }
              </div>
            </>
          ) : (
            <div style={S.emptyPane}>
              <div style={{ fontSize: 48 }}>📭</div>
              <p>Select an email to read</p>
            </div>
          )}
        </div>
      </div>

      {/* Compose Modal */}
      {compose && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 12, width: 520, maxWidth: '95vw', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700 }}>✏ New Message</h3>
              <button style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#6b7280' }} onClick={() => setCompose(false)}>✕</button>
            </div>
            <form onSubmit={handleCompose} style={{ padding: 20 }}>
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: 12, color: '#6b7280', marginBottom: 4 }}>To</label>
                <input style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: 6, padding: '8px 10px', fontSize: 14, boxSizing: 'border-box' }} type="email" value={composeTo} onChange={e => setComposeTo(e.target.value)} required placeholder="recipient@example.com" />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Subject</label>
                <input style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: 6, padding: '8px 10px', fontSize: 14, boxSizing: 'border-box' }} type="text" value={composeSubject} onChange={e => setComposeSubject(e.target.value)} required placeholder="Subject" />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Message</label>
                <textarea style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: 6, padding: '8px 10px', fontSize: 14, minHeight: 160, resize: 'vertical', boxSizing: 'border-box' }} value={composeBody} onChange={e => setComposeBody(e.target.value)} required placeholder="Write your message..." />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button type="button" style={{ background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 6, padding: '8px 16px', cursor: 'pointer', fontSize: 13 }} onClick={() => setCompose(false)}>Cancel</button>
                <button type="submit" disabled={composeSending} style={{ background: '#DC143C', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 16px', cursor: composeSending ? 'wait' : 'pointer', fontSize: 13, fontWeight: 600 }}>
                  {composeSending ? 'Sending…' : '📤 Send'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {ConfirmDialog}
    </div>
  );
}

function fmtName(email = '') {
  const m = email.match(/^(.+?)\s*<.+>$/);
  if (m) return m[1].replace(/"/g, '');
  return email.split('@')[0];
}

function fmtDate(ds) {
  const d = new Date(ds);
  const now = new Date();
  const diff = Math.floor((now - d) / 86400000);
  if (diff === 0) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (diff === 1) return 'Yesterday';
  if (diff < 7) return d.toLocaleDateString([], { weekday: 'short' });
  return d.toLocaleDateString();
}

function fmtDateTime(ds) {
  return new Date(ds).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}
